<template>
  <v-container
    fluid
    data-cy="profile-info-container"
  >
    <v-row no-gutters>
      <v-col class="d-flex justify-start">
        <div class="rounded-circle illustration-container mr-4">
          <sn-illustration
            illustration="PersonalInfo"
            size="xs"
          />
        </div>
        <div class="d-flex justify-center align-start flex-column">
          <h2
            class="sn-h3"
            data-cy="profile-info-title"
          >
            Profile info
          </h2>
        </div>
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="info-row"
    >
      <v-col
        cols="6"
        class="pr-4"
      >
        <sn-text-field
          v-model="firstName"
          label="First name"
          data-cy="first-name-field"
          :rules="firstNameRules"
          :validate-on-blur="true"
        />
      </v-col>
      <v-spacer />
      <v-col
        cols="6"
        class="pr-4"
      >
        <sn-text-field
          v-model="lastName"
          label="Last name"
          data-cy="last-name-field"
          :rules="lastNameRules"
          :validate-on-blur="true"
        />
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="info-row"
    >
      <v-col
        cols="6"
        class="pr-4"
      >
        <sn-text-field
          v-model="email"
          label="Email"
          data-cy="profile-info-email-field"
          :rules="emailRules"
          :validate-on-blur="true"
          :disabled="true"
        />
      </v-col>
      <v-spacer />
      <v-col
        cols="6"
        class="pr-4"
      >
        <sn-text-field
          v-model="phone"
          label="Phone"
          data-cy="phone-field"
          :rules="phoneRules"
          :validate-on-blur="true"
          @blur="updatePhoneField"
        />
      </v-col>
    </v-row>
    <v-row
      v-if="false"
      no-gutters
      class="info-row"
    >
      <v-col
        cols="6"
        class="pr-4"
      >
        <sn-text-field
          v-model="name"
          label="Company"
          :rules="firstNameRules"
          :validate-on-blur="true"
        />
      </v-col>
      <v-spacer />
      <v-col
        cols="6"
        class="pr-4"
      >
        <sn-text-field
          v-model="name"
          label="Job title"
          :rules="firstNameRules"
          :validate-on-blur="true"
        />
      </v-col>
    </v-row>
    <v-row
      no-gutters
      class="info-row"
    >
      <v-spacer />
      <v-col
        cols="2"
        class="pr-4"
      >
        <div class="d-flex align-end flex-column">
          <sn-btn
            style="marginTop: 10px"
            text="Save"
            type="primary"
            size="large"
            data-cy="profile-info-save-btn"
            :disabled="!saveEnabled"
            @click="handleSave"
          />
        </div>
      </v-col>
    </v-row>
    <sn-toast
      :value="showSuccessToast"
      :message="successToastMessage"
      event="success"
    />
    <sn-toast
      :value="showErrorToast"
      :message="errorToastMessage"
      event="error"
    />
  </v-container>
</template>

<script>
import snValidationsService from '~/services/snValidationsService.js'
import closingPortalService from '~/services/snClosingPortalService'

export default {
  name: 'ProfileInfo',
  props: {
    currentUser: {
      type: Object,
      default: null,
      required: false
    }
  },
  data () {
    return {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      profileInfo: [],
      showSuccessToast: false,
      showErrorToast: false,
      successToastMessage: '',
      errorToastMessage: ''
    }
  },

  computed: {
    firstNameRules () {
      return [v => snValidationsService.name(v || '') || 'First name is a required field']
    },
    lastNameRules () {
      return [v => snValidationsService.name(v || '') || 'Last name is a required field']
    },
    emailRules () {
      return [v => snValidationsService.email(v || '') || 'A valid email address is required']
    },
    phoneRules () {
      return [v => snValidationsService.phone(v || '') || 'A valid phone number is required']
    },
    saveEnabled () {
      return (
        this.firstName !== this.profileInfo.name ||
        this.lastName !== this.profileInfo.last_name ||
        this.email !== this.profileInfo.email ||
        this.unformatPhone(this.phone) !== this.profileInfo.unformatted_phone
      ) &&
        snValidationsService.name(this.firstName || '') &&
        snValidationsService.name(this.lastName || '') &&
        snValidationsService.phone(this.phone || '')
    }
  },

  watch: {
    currentUser () {
      closingPortalService.getMyAccountProfileInfo(this.currentUser.guid).then(resp => {
        this.setCurrentUserInData(resp.data.user)
      }).catch(err => {
        console.error(err)
      })
    }
  },

  methods: {
    handleSave () {
      closingPortalService.updateMyAccountProfileInfo(this.firstName, this.lastName, this.unformatPhone(this.phone)).then(resp => {
        const user = resp.data.update_user.user
        if (this.firstName === user.name && this.lastName === user.last_name && this.email === user.email && this.unformatPhone(this.phone) === user.unformatted_phone) {
          this.setCurrentUserInData(user)
          this.successToastMessage = 'Changes saved'
          this.showSuccessToast = true
          setTimeout(() => { this.showSuccessToast = false }, 6000)
        } else {
          this.errorToastMessage = 'Changes failed'
          this.showErrorToast = true
          setTimeout(() => { this.showErrorToast = false }, 6000)
        }
      })
        .catch(e => {
          this.errorToastMessage = 'An error occurred while updating this information. Please try again later.'
          this.showErrorToast = true
          setTimeout(() => { this.showErrorToast = false }, 6000)
        })
    },

    formatPhone (number) {
      const phArr = number.match(/^(\d{3})(\d{3})(\d{4})$/)
      if (phArr) {
        return `(${phArr[1]}) ${phArr[2]}-${phArr[3]}`
      }
    },

    unformatPhone (number) {
      return number ? number.replace(/\D/g, '') : ''
    },

    updatePhoneField () {
      if (snValidationsService.phone(this.phone || '')) {
        this.phone = this.formatPhone(this.unformatPhone(this.phone))
      }
    },

    setCurrentUserInData (user) {
      this.profileInfo = user
      this.firstName = user.name
      this.lastName = user.last_name
      this.email = user.email
      this.phone = this.formatPhone(user.unformatted_phone)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~/assets/css/snui';

.illustration-container {
  background-color: $sn-brand-01;
  width: 112px;
  height: 112px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.info-row {
  width: 100%;
  margin-top: 36px;
}

</style>
